/**
 * @file This file contains the TutorialManager class for guiding new players.
 * @module TutorialManager
 */

import { tutorialsConfig } from '../config/tutorialsConfig.js';
import { tooltipsConfig } from '../config/tooltipsConfig.js';

export class TutorialManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./BannerManager.js').BannerManager} dependencies.bannerManager - The banner manager instance.
     * @param {import('./TooltipManager.js').TooltipManager} dependencies.tooltipManager - The tooltip manager instance.
     * @param {import('./PlayerStatsManager.js').PlayerStatsManager} dependencies.playerStatsManager - The player stats manager instance.
     */
    constructor({ eventEmitter, ui, bannerManager, tooltipManager, playerStatsManager }) {
        this.eventEmitter = eventEmitter;
        this.ui = ui;
        this.bannerManager = bannerManager;
        this.tooltipManager = tooltipManager;
        this.playerStatsManager = playerStatsManager;

        this.activeTutorial = null;
        this.currentStepIndex = -1;
        this.isWaitingForAction = false;

        this.eventEmitter.on('game:start', ({ gameMode }) => this.start('first-game-tutorial', gameMode));
    }

    /**
     * Starts a tutorial sequence by its ID.
     * @param {string} tutorialId - The ID of the tutorial from the config.
     * @param {string} [gameMode] - The current game mode.
     */
    start(tutorialId, gameMode) {
        const tutorial = tutorialsConfig[tutorialId];
        if (!tutorial) return;

        // Check conditions before starting
        if (tutorial.condition === 'isFirstPlay' && !this.playerStatsManager.isFirstPlay(false)) {
            return;
        }

        // Do not run this tutorial in rolling mode.
        if (gameMode === 'rolling') {
            // Since the tutorial is being skipped, we should mark the first play as complete
            // to prevent it from trying to run on subsequent non-rolling games.
            this.playerStatsManager.markFirstPlayComplete();
            return;
        }

        this.activeTutorial = tutorial;
        this.currentStepIndex = -1;
        this.nextStep();
    }

    /**
     * Advances the tutorial to the next step.
     */
    nextStep() {
        if (!this.activeTutorial) return;

        // Cleanup previous step's UI effects
        this.ui.toggleDimmer(false);
        this.ui.hideArrow();
        this.ui.hideHighlightBox();
        const previousStep = this.activeTutorial.steps[this.currentStepIndex];
        if (previousStep && previousStep.type === 'tooltip') {
            this.tooltipManager.hide(previousStep.tooltipId);
        }

        this.currentStepIndex++;
        if (this.currentStepIndex >= this.activeTutorial.steps.length) {
            this.endTutorial();
            return;
        }

        const step = this.activeTutorial.steps[this.currentStepIndex];
        this.processStep(step);
    }

    processStep(step) {
        if (step.highlight) this.ui.toggleDimmer(true, step.target);
        if (step.arrow) this.ui.showArrow(step.target, step.arrow);
        if (step.highlightBox) this.ui.showHighlightBox(step.highlightBox);

        switch (step.type) {
        case 'banner':
            this.bannerManager.show(step.content, step.duration);
            setTimeout(() => this.nextStep(), step.duration);
            break;
        case 'tooltip':
            this.tooltipManager.show(step.tooltipId, step.target);
            const tooltipConfig = tooltipsConfig[step.tooltipId];
            // If the tooltip is configured to be dismissed by an action,
            // the tutorial should wait for that action to advance.
            if (tooltipConfig?.dismiss?.action) {
                this.isWaitingForAction = true;
                this.eventEmitter.once(tooltipConfig.dismiss.action, () => {
                    // If this action completes the first-play tutorial, mark it.
                    if (this.activeTutorial.condition === 'isFirstPlay') {
                        this.playerStatsManager.markFirstPlayComplete();
                    }
                    this.isWaitingForAction = false;
                    this.nextStep();
                });
            }
            break;
        case 'wait-for-action':
            this.isWaitingForAction = true;
            this.eventEmitter.once(step.action, () => {
                this.isWaitingForAction = false;
                this.nextStep();
            });
            break;
        }
    }

    endTutorial() {
        this.activeTutorial = null;
        this.currentStepIndex = -1;
        this.ui.toggleDimmer(false);
        this.ui.hideArrow();
        this.ui.hideHighlightBox();
    }
}